import {
    Box,
    Button,
    FormControl,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useDueDaysField from 'shared/hooks/useDueDaysField';
import {
    getDefaultInvoiceDueDays,
    type TimeUnit,
} from 'shared/utils/invoicing';

import { BackLinkButton } from '@/components';
import { useUserContext } from '@/context/UserContext';
import {
    getLocalDefaultInvoiceDueDays,
    updateDefaultInvoiceDueDays,
} from '@/helpers/organisations';
import { ROUTE } from '@/router/routes';
import Logger from '@/services/logger';
import { Snackbar } from '@/services/toastNotifications';

function DefaultInvoiceDueDaysPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { userProfileData } = useUserContext();

    const [storedDueDays, setStoredDueDays] = useState<number | undefined>(
        undefined,
    );

    const refreshStoredDueDays = useCallback(async () => {
        setStoredDueDays(await getLocalDefaultInvoiceDueDays());
    }, []);

    useEffect(() => {
        refreshStoredDueDays();
    }, [refreshStoredDueDays]);

    const {
        defaultDueDays,
        dueValue,
        dueUnitType,
        parsedDueDays,
        dueValueError,
        resolveDueValue,
        setDueValue,
        setDueUnitType,
    } = useDueDaysField(storedDueDays, t);
    const [isSaving, setIsSaving] = useState(false);

    const isValid = !dueValueError;

    const isDirty =
        parsedDueDays !== null &&
        parsedDueDays !== getDefaultInvoiceDueDays(storedDueDays);

    const handleSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (!isValid || !isDirty || isSaving || parsedDueDays === null) {
                return;
            }

            if (!userProfileData?.id) {
                Logger.logError(
                    'DefaultInvoiceDueDaysPage: no user id, aborting save',
                );
                Snackbar.showToastNotification({
                    message: t('App:Messages:something_went_wrong'),
                    options: { variant: 'error' },
                });
                return;
            }

            setIsSaving(true);
            try {
                await updateDefaultInvoiceDueDays({
                    totalDays: parsedDueDays,
                    userId: userProfileData.id,
                });
                await refreshStoredDueDays();

                Snackbar.showToastNotification({
                    message: t('App:Messages:has_been_edited_successfully', {
                        entity: t(
                            'Settings:invoices_admin:default_due_days_header',
                        ),
                    }),
                });
                navigate(ROUTE.settings);
            } catch (error) {
                Logger.logError(
                    `DefaultInvoiceDueDaysPage: failed to update default invoice due days ${error}`,
                );
                Snackbar.showToastNotification({
                    message: t('App:Messages:something_went_wrong'),
                    options: { variant: 'error' },
                });
            } finally {
                setIsSaving(false);
            }
        },
        [
            refreshStoredDueDays,
            isDirty,
            isSaving,
            isValid,
            navigate,
            t,
            parsedDueDays,
            userProfileData?.id,
        ],
    );

    const backButton = (
        <BackLinkButton
            to={ROUTE.settings}
            text={t('Options:button:settings')}
            testID="DefaultInvoiceDueDaysPage-BackButton"
        />
    );

    return (
        <Box sx={{ pb: 4 }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 4,
                }}
            >
                {backButton}
            </Box>

            <Box sx={{ maxWidth: 650 }}>
                <Typography sx={{ mb: 1, fontWeight: 'bold', fontSize: 14 }}>
                    {t('Settings:invoices_admin:default_due_days_section')}
                </Typography>
                <Typography sx={{ mb: 3, fontSize: 14 }}>
                    {t('Settings:invoices_admin:default_due_days_explanation')}
                </Typography>

                <form onSubmit={handleSubmit}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            name="value"
                            type="text"
                            size="small"
                            inputMode="numeric"
                            autoComplete="off"
                            value={dueValue}
                            onChange={(e) => setDueValue(e.target.value)}
                            onBlur={resolveDueValue}
                            error={!isValid}
                            helperText={dueValueError ?? ''}
                            placeholder={String(defaultDueDays)}
                            sx={{ flex: 1 }}
                        />
                        <FormControl sx={{ flex: 1 }}>
                            <Select
                                size="small"
                                value={dueUnitType}
                                onChange={(e) =>
                                    setDueUnitType(e.target.value as TimeUnit)
                                }
                                data-test-id="DefaultInvoiceDueDaysPage-UnitSelect"
                            >
                                <MenuItem value="days">
                                    {t('Invoice:time_options:days')}
                                </MenuItem>
                                <MenuItem value="weeks">
                                    {t('Invoice:time_options:weeks')}
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    <Typography
                        variant="caption"
                        sx={{
                            display: 'block',
                            mb: 2,
                            color: 'text.secondary',
                        }}
                    >
                        {t('Settings:invoices_admin:default_due_days_tip')}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate(ROUTE.settings)}
                            disabled={isSaving}
                            data-test-id="DefaultInvoiceDueDaysPage-CancelButton"
                        >
                            {t('Actions:cancel')}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={!isValid || !isDirty || isSaving}
                            data-test-id="DefaultInvoiceDueDaysPage-SaveButton"
                        >
                            {t('Actions:save')}
                        </Button>
                    </Box>
                </form>
            </Box>
        </Box>
    );
}

export default DefaultInvoiceDueDaysPage;
